// frontend/src/pages/lobby/LobbyAttendeeSettingsPage.tsx
import { useEffect, useRef, useState } from 'react'
import { Box, Button, Field, Flex, Heading, HStack, Input, Spinner, Switch, Text, VStack } from '@chakra-ui/react'
import { FiChevronLeft } from 'react-icons/fi'
import { Link, useParams } from 'react-router-dom'
import { useAttendeeSchedule } from '../../hooks/useLobbyDay'
import { useAttendeeProfile } from '../../hooks/useAttendeeProfile'
import { usePreference } from '../../preferences/usePreference'
import { AttendeeDeadEnd } from '../../components/lobby/AttendeeDeadEnd'

const THEMES = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
] as const

type PendingAction = { kind: 'load' } | { kind: 'save'; patch: { nickName: string; phone: string; email: string } }

export function LobbyAttendeeSettingsPage() {
  const { token } = useParams<{ token: string }>()
  const { data, isLoading, error } = useAttendeeSchedule(token)
  const [theme, setTheme] = usePreference('appearance.theme')
  const [reduceMotion, setReduceMotion] = usePreference('appearance.reduceMotion')
  const { load, save, acceptTerms, profile, termsRequired, unavailable, isBusy } = useAttendeeProfile(token)

  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({ nickName: '', phone: '', email: '' })
  const pendingRef = useRef<PendingAction>({ kind: 'load' })
  const seededRef = useRef(false)

  // Seed the edit form from `profile` the first time it's available while editing.
  // Guarded so it doesn't clobber in-progress edits on later re-renders.
  useEffect(() => {
    if (!editing) { seededRef.current = false; return }
    if (profile && !seededRef.current) {
      setForm({ nickName: profile.nickName ?? '', phone: profile.phone ?? '', email: profile.email ?? '' })
      seededRef.current = true
    }
  }, [editing, profile])

  const beginEdit = () => {
    setEditing(true)
    pendingRef.current = { kind: 'load' }
    void load()
  }

  const submitSave = () => {
    const patch = { nickName: form.nickName, phone: form.phone, email: form.email }
    pendingRef.current = { kind: 'save', patch }
    void save(patch)
  }

  const acceptAndContinue = () => {
    const pending = pendingRef.current
    if (pending.kind === 'save') void acceptTerms('save', pending.patch)
    else void acceptTerms('load')
  }

  if (isLoading) return <Flex minH="100vh" align="center" justify="center"><Spinner size="lg" color="accent" /></Flex>
  if (!!error || !data) return <AttendeeDeadEnd currentToken={token} />

  return (
    <Box minH="100vh" bg="canvas">
      {/* Foreground uses the band.fg token (mode-invariant light color for the
          always-dark band), not a raw hardcoded literal -- see theme.ts. */}
      <Box bg="band" color="band.fg" px={4} py={3}>
        <Flex align="center" gap={2}>
          <Box
            asChild
            aria-label="Back"
            minW="44px"
            minH="44px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="md"
            _active={{ opacity: 0.7 }}
          >
            <Link to={`/lobby/${token}`}><FiChevronLeft /></Link>
          </Box>
          <Heading size="sm">Settings</Heading>
        </Flex>
      </Box>

      <VStack align="stretch" gap={4} p={4} maxW="480px" mx="auto">
        {/* Appearance */}
        <Box bg="surface" borderWidth="1px" borderColor="border" borderRadius="lg" p={4}>
          <Heading size="xs" color="ink" mb={3}>Appearance</Heading>
          <Text fontSize="sm" color="fg.muted" mb={1}>Theme</Text>
          <HStack gap={2} mb={4}>
            {THEMES.map((t) => (
              <Button
                key={t.value}
                flex={1}
                minH="44px"
                variant={theme === t.value ? 'solid' : 'outline'}
                colorPalette={theme === t.value ? 'brand' : 'gray'}
                onClick={() => setTheme(t.value)}
              >
                {t.label}
              </Button>
            ))}
          </HStack>
          <Switch.Root
            checked={reduceMotion === true}
            onCheckedChange={(e) => setReduceMotion(e.checked === true)}
            colorPalette="brand"
            minH="44px"
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Switch.HiddenInput />
            <Switch.Label fontSize="sm" color="ink">Reduce motion</Switch.Label>
            <Switch.Control>
              <Switch.Thumb />
            </Switch.Control>
          </Switch.Root>
        </Box>

        {/* Your info */}
        <Box bg="surface" borderWidth="1px" borderColor="border" borderRadius="lg" p={4}>
          <Heading size="xs" color="ink" mb={2}>Your info</Heading>
          <Text fontSize="sm" color="fg.muted">Signed in as</Text>
          <Text fontSize="md" color="ink" fontWeight="semibold" mb={3}>{data?.attendeeName}</Text>

          {unavailable ? (
            <Text fontSize="sm" color="fg.muted">
              Your info isn't available yet — check with your organizer.
            </Text>
          ) : termsRequired ? (
            <VStack align="stretch" gap={3}>
              <Text fontSize="sm" color="ink">
                You're editing your own contact info for this lobby day.
              </Text>
              <Text fontSize="sm" color="ink">
                This information is used only for this lobby day's advocacy and is visible to your organizers.
              </Text>
              <Button minH="44px" colorPalette="brand" loading={isBusy} onClick={acceptAndContinue}>
                I agree
              </Button>
            </VStack>
          ) : !editing || !profile ? (
            <Button minH="44px" variant="outline" loading={isBusy} onClick={beginEdit}>
              Edit your info
            </Button>
          ) : (
            <VStack align="stretch" gap={3}>
              <ProfileField
                label="Preferred name"
                value={form.nickName}
                onChange={(v) => setForm((f) => ({ ...f, nickName: v }))}
              />
              <ProfileField
                label="Phone"
                value={form.phone}
                onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
                type="tel"
              />
              <ProfileField
                label="Email"
                value={form.email}
                onChange={(v) => setForm((f) => ({ ...f, email: v }))}
                type="email"
              />
              <Button minH="44px" colorPalette="brand" loading={isBusy} onClick={submitSave}>
                Save
              </Button>
            </VStack>
          )}
        </Box>
      </VStack>
    </Box>
  )
}

function ProfileField({
  label, value, onChange, type = 'text',
}: { label: string; value: string; onChange: (v: string) => void; type?: string }) {
  return (
    <Field.Root>
      <Field.Label fontSize="sm" color="fg.muted">{label}</Field.Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        minH="44px"
      />
    </Field.Root>
  )
}
